// Uptime math on the client (Observability §5.8; D-67): the API sends per-day minutes, the page sums the days it
// shows (90, or 30 on narrow screens) and truncates — never rounds — the percentage to 2 decimals.

export interface DayMinutes {
  upMinutes: number
  downMinutes: number
}

/** Σ up / Σ (up + down) over `days`, or null ("No data") when nothing was observed. */
export function uptimeOf(days: readonly DayMinutes[]): number | null {
  let up = 0
  let down = 0
  for (const d of days) {
    up += d.upMinutes
    down += d.downMinutes
  }
  return up + down === 0 ? null : up / (up + down)
}

/** "99.93" from minutes: truncated to 2 decimals with integer math, so any downtime stays below 100.00. */
export function pctFromMinutes(up: number, down: number): string | null {
  if (up + down === 0) return null
  return (Math.floor((up * 10_000) / (up + down)) / 100).toFixed(2)
}

/** The days' uptime as "XX.XX", truncated. */
export function uptimePct(days: readonly DayMinutes[]): string | null {
  let up = 0
  let down = 0
  for (const d of days) {
    up += d.upMinutes
    down += d.downMinutes
  }
  return pctFromMinutes(up, down)
}

/** A ratio (0..1, e.g. S-07's `overall.uptime`) as "XX.XX", truncated; null → null. */
export function pctFromRatio(ratio: number | null | undefined): string | null {
  if (ratio === null || ratio === undefined) return null
  return (Math.floor(Math.round(ratio * 1_000_000) / 100) / 100).toFixed(2)
}

/** The mockup's `fmtDuration`: "9 min", "1 h 5 min", "2 h". */
export function fmtDuration(min: number): string {
  if (min < 60) return `${String(min)} min`
  const h = Math.floor(min / 60)
  const m = min % 60
  return m === 0 ? `${String(h)} h` : `${String(h)} h ${String(m)} min`
}
